import React, { useEffect, useState } from "react";
import {
  Box, Button, Checkbox, CircularProgress, Dialog, DialogContent,
  FormControlLabel, IconButton, InputAdornment, TextField, Tooltip, Typography,
} from "@mui/material";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { useNotifier } from "../../Core/Notifier";
import { API_URL } from "../../config";

const T = {
  navy: "#0d1b2a", gold: "#c9a96e", white: "#fff",
  bg: "#f7f5f1", slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
  greenTx: "#2d6a35", greenBg: "#edf7ee",
  peach: "#ffebd3", inputBg: "#f8f7f5",
};

const inputSx = {
  "& .MuiInputBase-root": { fontSize: 13.5, borderRadius: "10px", bgcolor: T.inputBg, border: `1px solid ${T.line}`, "&.Mui-focused": { bgcolor: T.peach, borderColor: T.gold }, transition: "all 0.15s", "& fieldset": { border: "none" } },
  "& .MuiInputLabel-root": { fontSize: 13 }, "& input": { py: "9px" },
};

const hdrs = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") || ""}` });
const post = (p: string, b: any) => fetch(`${API_URL}${p}`, { method: "POST", headers: hdrs(), body: JSON.stringify(b) }).then(r => r.json());

const generatePassword = () => {
  const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let pw = "";
  for (let i = 0; i < 10; i++) pw += chars[Math.floor(Math.random() * chars.length)];
  return pw + "A1!";
};

interface Props {
  open: boolean;
  vendorId: number | null;
  vendorName: string;
  vendorEmail: string;
  hasExistingCredentials?: boolean;
  onClose: () => void;
  onSkip?: () => void;
  onCreated?: () => void;
}

const VendorCredentialsModal: React.FC<Props> = ({
  open, vendorId, vendorName, vendorEmail, hasExistingCredentials = false,
  onClose, onSkip, onCreated,
}) => {
  const { showMessage, showErrorMessage } = useNotifier();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mustChangePassword, setMustChangePassword] = useState(true);
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const isResetMode = hasExistingCredentials;
  const portalUrl = `${window.location.origin}/vendor/login`;

  useEffect(() => {
    if (open) {
      setUsername(vendorEmail || "");
      const pw = generatePassword();
      setPassword(pw);
      setConfirmPassword(pw);
      setMustChangePassword(true);
      setCreated(false);
      setShowPw(false);
    }
  }, [open, vendorEmail]);

  const handleCreate = async () => {
    if (!vendorId) return;
    if (!username.trim()) { showErrorMessage("Username/email is required"); return; }
    if (!password.trim() || password.length < 6) { showErrorMessage("Password must be at least 6 characters"); return; }
    if (password !== confirmPassword) { showErrorMessage("Passwords do not match"); return; }

    setSaving(true);
    const endpoint = isResetMode
      ? `/vendors/manage/${vendorId}/reset-password`
      : `/vendors/manage/${vendorId}/credentials`;

    const body = isResetMode
      ? { password, must_change_password: mustChangePassword }
      : { username, password, must_change_password: mustChangePassword };

    const res = await post(endpoint, body);
    if (res.code === 200 && res.status) {
      showMessage(res.message);
      setCreated(true);
      onCreated?.();
    } else {
      showErrorMessage(res.message || "Failed to create credentials");
    }
    setSaving(false);
  };

  const handleSendEmail = async () => {
    if (!vendorId) return;
    setSendingEmail(true);
    const res = await post(`/vendors/manage/${vendorId}/send-credentials`, {});
    if (res.code === 200 && res.status) showMessage("Credentials email sent!");
    else showErrorMessage(res.message || "Failed to send email");
    setSendingEmail(false);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    showMessage(`${label} copied to clipboard`);
  };

  const copyAll = () => {
    const text = `Vendor Portal Login\n\nURL: ${portalUrl}\nUsername: ${username}\nPassword: ${password}\n\n${mustChangePassword ? "You will be asked to change your password on first login." : ""}`;
    navigator.clipboard.writeText(text);
    showMessage("All credentials copied to clipboard");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${T.navy} 0%, #1a3550 100%)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: "rgba(201,169,110,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <VpnKeyOutlinedIcon sx={{ fontSize: 16, color: T.gold }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.white }}>
              {isResetMode ? "Reset Portal Password" : "Vendor Portal Credentials"}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>{vendorName}</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {created ? (
          /* ── Success State ── */
          <Box>
            <Box sx={{ textAlign: "center", py: 2, mb: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 48, color: T.greenTx, mb: 1 }} />
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.navy }}>
                {isResetMode ? "Password Reset Successfully!" : "Credentials Created!"}
              </Typography>
              <Typography sx={{ fontSize: 12, color: T.slateLt, mt: 0.5 }}>
                Share the details below with the vendor to grant portal access.
              </Typography>
            </Box>

            <Box sx={{ bgcolor: T.bg, borderRadius: "12px", p: 2.5, mb: 2 }}>
              {/* Portal URL */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.05em" }}>Vendor Login URL</Typography>
                <Tooltip title="Copy URL"><IconButton size="small" onClick={() => copyToClipboard(portalUrl, "URL")}><ContentCopyIcon sx={{ fontSize: 12 }} /></IconButton></Tooltip>
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy, mb: 2, wordBreak: "break-all" }}>{portalUrl}</Typography>

              {/* Username */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.05em" }}>Username</Typography>
                <Tooltip title="Copy username"><IconButton size="small" onClick={() => copyToClipboard(username, "Username")}><ContentCopyIcon sx={{ fontSize: 12 }} /></IconButton></Tooltip>
              </Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy, mb: 2 }}>{username}</Typography>

              {/* Password */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.05em" }}>Password</Typography>
                <Tooltip title="Copy password"><IconButton size="small" onClick={() => copyToClipboard(password, "Password")}><ContentCopyIcon sx={{ fontSize: 12 }} /></IconButton></Tooltip>
              </Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy, fontFamily: "monospace" }}>{password}</Typography>

              {mustChangePassword && (
                <Typography sx={{ fontSize: 10, color: T.gold, fontWeight: 600, mt: 2, fontStyle: "italic" }}>
                  Vendor will be asked to change password on first login.
                </Typography>
              )}
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button fullWidth variant="outlined" onClick={copyAll} startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: T.line, color: T.navy, borderRadius: "10px", "&:hover": { borderColor: T.gold, bgcolor: `${T.gold}08` } }}>
                Copy All
              </Button>
              <Button fullWidth variant="outlined" onClick={handleSendEmail} disabled={sendingEmail}
                startIcon={sendingEmail ? <CircularProgress size={12} /> : <SendOutlinedIcon sx={{ fontSize: 14 }} />}
                sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: T.line, color: T.navy, borderRadius: "10px", "&:hover": { borderColor: T.gold, bgcolor: `${T.gold}08` } }}>
                {sendingEmail ? "Sending..." : "Send Email"}
              </Button>
            </Box>
          </Box>
        ) : (
          /* ── Form State ── */
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography sx={{ fontSize: 12, color: T.slateLt }}>
              {isResetMode
                ? `Reset the portal password for ${vendorName}.`
                : `Set up portal login credentials for ${vendorName}. They can access their events, assignments, and notifications.`}
            </Typography>

            {/* Username */}
            {!isResetMode && (
              <TextField label="Username (Email) *" value={username} onChange={e => setUsername(e.target.value)} size="small" sx={inputSx}
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon sx={{ fontSize: 14, color: T.slateLt }} /></InputAdornment> }} />
            )}

            {/* Password */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 1, alignItems: "start" }}>
              <TextField label="Password *" value={password} onChange={e => setPassword(e.target.value)} size="small"
                type={showPw ? "text" : "password"} sx={inputSx}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><VpnKeyOutlinedIcon sx={{ fontSize: 14, color: T.slateLt }} /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPw(!showPw)}>
                        {showPw ? <VisibilityOffOutlinedIcon sx={{ fontSize: 14 }} /> : <VisibilityOutlinedIcon sx={{ fontSize: 14 }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }} />
              <Tooltip title="Generate new password">
                <IconButton size="small" onClick={() => { const pw = generatePassword(); setPassword(pw); setConfirmPassword(pw); }}
                  sx={{ mt: 0.5, bgcolor: T.bg, "&:hover": { bgcolor: T.peach } }}>
                  <RefreshOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Confirm Password */}
            <TextField label="Confirm Password *" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} size="small"
              type={showPw ? "text" : "password"} sx={inputSx}
              error={confirmPassword.length > 0 && password !== confirmPassword}
              helperText={confirmPassword.length > 0 && password !== confirmPassword ? "Passwords do not match" : ""}
              InputProps={{ startAdornment: <InputAdornment position="start"><VpnKeyOutlinedIcon sx={{ fontSize: 14, color: T.slateLt }} /></InputAdornment> }} />

            {/* Must change password */}
            <FormControlLabel
              control={<Checkbox checked={mustChangePassword} onChange={e => setMustChangePassword(e.target.checked)} size="small" sx={{ color: T.slateLt, "&.Mui-checked": { color: T.gold } }} />}
              label={<Typography sx={{ fontSize: 12, color: T.slate }}>Require password change on first login</Typography>}
            />

            {/* Portal URL preview */}
            <Box sx={{ bgcolor: T.bg, borderRadius: "10px", p: 1.5 }}>
              <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.3 }}>Vendor Login URL</Typography>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.navy, wordBreak: "break-all" }}>{portalUrl}</Typography>
                <Tooltip title="Copy URL"><IconButton size="small" onClick={() => copyToClipboard(portalUrl, "URL")}><ContentCopyIcon sx={{ fontSize: 11 }} /></IconButton></Tooltip>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: `1px solid ${T.line}` }}>
        {created ? (
          <Button variant="contained" onClick={onClose}
            sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 3, "&:hover": { bgcolor: "#1a3550" } }}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onSkip || onClose} sx={{ textTransform: "none", fontSize: 13, color: T.slate }}>
              {onSkip ? "Skip" : "Cancel"}
            </Button>
            <Button variant="contained" onClick={handleCreate} disabled={saving || !password.trim() || password !== confirmPassword || (!isResetMode && !username.trim())}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <VpnKeyOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 3, "&:hover": { bgcolor: "#1a3550" }, "&.Mui-disabled": { bgcolor: T.line } }}>
              {saving ? "Creating..." : isResetMode ? "Reset Password" : "Create Credentials"}
            </Button>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default VendorCredentialsModal;
